import { ref } from 'vue';
import { useQuasar } from 'quasar';
import { t } from 'boot/i18n';

export function useClipboard() {
  const $q = useQuasar();
  const copied = ref(false);

  async function copy(text: string): Promise<boolean> {
    try {
      // Method 1: Modern Clipboard API (works on HTTPS/localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      // Method 2: execCommand fallback (works everywhere)
      return execCommandCopy(text);
    } catch (error) {
      console.error('Clipboard copy failed:', error);

      // Method 3: Show dialog for manual copy (mobile fallback)
      if ($q.platform.is.mobile) {
        showCopyDialog(text);
        return true; // Consider it successful since we showed the dialog
      }

      return false;
    }
  }

  function execCommandCopy(text: string): boolean {
    try {
      // Create temporary textarea
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // Make it invisible but functional
      Object.assign(textArea.style, {
        position: 'fixed',
        left: '-9999px',
        top: '-9999px',
        opacity: '0',
        pointerEvents: 'none',
        zIndex: '-1',
        width: '1px',
        height: '1px',
      });

      textArea.setAttribute('readonly', '');
      textArea.setAttribute('contenteditable', 'true');

      // Add to DOM
      document.body.appendChild(textArea);

      // Select text
      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      // Copy
      const successful = document.execCommand('copy');

      // Cleanup
      document.body.removeChild(textArea);

      return successful;
    } catch (error) {
      console.error('execCommand copy failed:', error);
      return false;
    }
  }

  function showCopyDialog(text: string) {
    $q.dialog({
      title: t('COPY_TO_CLIPBOARD'),
      message: t('MANUAL_COPY_INSTRUCTION'),
      prompt: {
        model: text,
        type: 'textarea',
        readonly: true,
        filled: true,
        autogrow: true,
      },
      ok: {
        label: t('CLOSE'),
        color: 'primary',
        flat: true,
      },
      cancel: false,
      persistent: true,
      class: 'copy-dialog',
    }).onOk(() => {
      // User closed the dialog, consider it copied
      copied.value = true;
      setTimeout(() => {
        copied.value = false;
      }, 2000);
    });
  }

  return {
    copy,
    copied,
  };
}
